/**
 * PlacementActionBar Component
 *
 * 行動版任務放置模式的底部操作區。
 * 包含半透明遮罩層 + 底部操作列（蓋住底部導航列）。
 *
 * 固定佈局（避免狀態切換時跳動）：
 * - 第一行：任務名稱（固定位置）
 * - 第二行：提示文字 / 時段資訊
 * - 右側：操作按鈕
 */
import React from 'react';
import { Task } from '../../../types/task';
import { format } from 'date-fns';
import { X, Check, MapPin } from 'lucide-react';

export interface PlacementActionBarProps {
  /** 被選取的任務 */
  selectedTask: Task;

  /** 預覽的時段資訊（null 表示狀態 1，有值表示狀態 2） */
  previewSlot: {
    startTime: Date;
    endTime: Date;
  } | null;

  /** 取消按鈕點擊回調 */
  onCancel: () => void;

  /** 確認按鈕點擊回調（僅狀態 2 可用） */
  onConfirm?: () => void;
}

/**
 * 格式化預估時間顯示
 */
function formatEstimatedTime(minutes: number | undefined): string {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes}min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
}

const PlacementActionBar: React.FC<PlacementActionBarProps> = ({
  selectedTask,
  previewSlot,
  onCancel,
  onConfirm,
}) => {
  const isSelecting = !previewSlot;
  const isPreviewing = !!previewSlot;

  return (
    <>
      {/* 遮罩層 */}
      <div
        className={`fixed inset-0 z-placement-bar ${
          isPreviewing ? 'bg-black/20' : 'bg-black/10 pointer-events-none'
        }`}
        onClick={isPreviewing ? onCancel : undefined}
        aria-hidden="true"
      />

      {/* 底部操作列 - 固定佈局，蓋住底部導航列 */}
      <div className="fixed bottom-0 left-0 right-0 z-placement-bar bg-white shadow-[0_-4px_12px_rgba(0,0,0,0.1)] px-4 pt-3 pb-4">
        <div className="flex items-center justify-between">
          {/* 左側：任務資訊（固定位置） */}
          <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">
              {selectedTask.title}
            </div>
            {/* 第二行：提示 or 時段 */}
            {isSelecting ? (
              <div className="flex items-center gap-1 text-sm text-sky-600 mt-0.5">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span>Tap a time slot to place</span>
              </div>
            ) : (
              <div className="text-sm text-gray-500 mt-0.5">
                {format(previewSlot.startTime, 'HH:mm')} - {format(previewSlot.endTime, 'HH:mm')}
                {selectedTask.estimatedTime && (
                  <span className="ml-2 text-gray-400">
                    {formatEstimatedTime(selectedTask.estimatedTime)}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* 右側：操作按鈕 */}
          <div className="flex items-center gap-2 ml-3">
            <button
              onClick={onCancel}
              className="px-3 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
              aria-label={isSelecting ? 'Cancel placement' : 'Cancel and reselect time slot'}
            >
              <X className="w-4 h-4" />
              Cancel
            </button>

            {isPreviewing && (
              <button
                onClick={onConfirm}
                className="px-4 py-2 text-sm font-medium bg-sky-600 text-white rounded-md hover:bg-sky-700 transition-colors flex items-center gap-1"
                aria-label="Confirm and create TimeBox"
              >
                <Check className="w-4 h-4" />
                Confirm
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PlacementActionBar;
